import type { ImageItem, Item } from '@messenger/types'

type SyncImageEditToEditorPagesArgs = {
  editedUrl: string | undefined
  imageEdits: ImageItem['image']['edits']
  /**
   * Id of the accepted AI alteration whose output is `editedUrl`. Left out
   * (undefined) by every write that renders from the source photo, such as
   * a Pintura export, a quick action or an image swap, so the item's AI
   * provenance clears automatically whenever the pixels stop being the
   * cutout. Only the Remove Background tool, and a Pintura export made from
   * the cutout, set it.
   */
  aiVariantId?: string
  /**
   * The whole stored cutout of `aiVariantId` (see ImageItem.image.aiVariantUrl),
   * given and cleared with it.
   */
  aiVariantUrl?: string
  // Setter for editorPagesAtom items array (for the current page)
  setEditorPagesItems: (items: Item[]) => void
  setEditedImageUrl: (url: string | undefined) => void
  setImageEdits: (edits: ImageItem['image']['edits']) => void
  setAiVariantId: (variantId: string | undefined) => void
  setAiVariantUrl: (url: string | undefined) => void
  // Optional: if provided, will also update the url field (used for image swaps)
  imageUrl?: string
  // Optional: if provided, will also update the url field via setter (used for image swaps)
  setImageUrl?: (url: string) => void
  // Callback that uses useRecoilTransaction_UNSTABLE to read latest state after updates
  // Returns the items array to be set outside the transaction (can't set selectors in transactions)
  syncItemsToEditorPages: () => Item[]
}

/**
 * Keeps image edit state in sync between the item atom family and editorPagesAtom.
 *
 * This follows the same pattern as useUpdatePageItems:
 * - Updates itemAtomFamily first (for immediate UI feedback)
 * - Then syncs the FULL list from itemAtomFamily to editorPagesAtom
 *   (not a partial update, which prevents deleted items from coming back)
 *
 * This ensures that:
 * - The currently selected item updates immediately for UI reactivity
 * - The backing `editorPagesAtom` state (which is saved as json_template) is
 *   always up to date with the latest edits
 * - Deleted items don't come back (because we sync the full list, not partial)
 * - New items are included (because we sync from itemAtomFamily, the source of truth)
 *
 * IMPORTANT:
 * - This utility should be used for any single-image edit entry point
 *   (quick actions, Pintura, AI alterations, etc.) so we never update one
 *   state store without updating the other.
 * - When swapping images, provide imageUrl and setImageUrl to ensure the url
 *   field is also synced between both stores.
 */
export const syncImageEditToEditorPages = ({
  editedUrl,
  imageEdits,
  aiVariantId,
  aiVariantUrl,
  setEditorPagesItems,
  setEditedImageUrl,
  setImageEdits,
  setAiVariantId,
  setAiVariantUrl,
  imageUrl,
  setImageUrl,
  syncItemsToEditorPages,
}: SyncImageEditToEditorPagesArgs) => {
  // First update the item atom family for immediate UI feedback
  setEditedImageUrl(editedUrl)
  setImageEdits(imageEdits)
  // Written on every edit path so an edit from the source photo clears the
  // AI provenance left by an earlier cutout.
  setAiVariantId(aiVariantId)
  setAiVariantUrl(aiVariantId === undefined ? undefined : aiVariantUrl)

  // If imageUrl is provided (e.g., during image swap), also update the url field
  if (imageUrl !== undefined && setImageUrl) {
    setImageUrl(imageUrl)
  }

  // Then sync the FULL list from itemAtomFamily to editorPagesAtom
  // The syncItemsToEditorPages callback uses useRecoilTransaction_UNSTABLE to read
  // the latest committed state (including our updates above), then we set it outside.
  // This ensures:
  // - Deleted items don't come back (they're not in itemAtomFamily)
  // - New items are included (they are in itemAtomFamily)
  // - We read the state AFTER our updates are committed
  // - We can't set selectors inside transactions, so we read in transaction and set outside
  const allItems = syncItemsToEditorPages()
  setEditorPagesItems(allItems)
}
